import { useState } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, ClipboardList, Flag, Gauge, GitBranch, LayoutDashboard, LockKeyhole, LogOut, Menu, Network, PlayCircle, Route as RouteIcon, ShieldCheck, Sparkles, Upload, X, Building2 } from "lucide-react";
import { CRUMB_PAGE, JOURNEY, PILLAR_NAV, navItemAllowed } from "./master";
import { navItemAllowedForApp } from "./g1Journey";
import { GuideContext } from "./guide";
import GovernanceCue from "../components/GovernanceCue";

const NAV_ICONS = {
  LayoutDashboard, Building2, ClipboardList, Upload, Sparkles, Gauge, Network, Flag, RouteIcon, ShieldCheck, GitBranch,
};

export const NAV = PILLAR_NAV.flatMap((pillar) => pillar.items.map(([label, href, icon]) => [label, href, NAV_ICONS[icon]]));

export const Badge = ({ children, tone = "blue" }) => (
  <span data-testid={`badge-${String(children).toLowerCase().replaceAll(" ", "-")}`} className={`status-badge ${tone}`}>{children}</span>
);

export const Title = ({ eyebrow, title, detail, action }) => (
  <div className="section-title">
    <div>
      <p className="kicker">{eyebrow}</p>
      <h2>{title}</h2>
      {detail && <p className="muted">{detail}</p>}
    </div>
    {action}
  </div>
);

export const gateTone = (state) => {
  if (state === "PASS") return "green";
  if (state === "READY" || state === "IN_REVIEW" || state === "IN_HYPERCARE") return "amber";
  if (state === "LOCKED" || state === "PENDING") return "slate";
  if (state === "NOT_APPLICABLE") return "slate";
  return "slate";
};

export const readableGate = (state) => (state || "").replace("_", " ");

export const recTone = (rec) => {
  const map = {
    "Refactor / Rearchitect": "cyan",
    "Replatform": "blue",
    "Rehost": "amber",
    "Repurchase": "amber",
    "Retire": "rose",
    "Retain": "slate",
  };
  return map[rec] || "blue";
};

export const PageWait = ({ label = "Loading application…", inline = false }) => (
  <div className={inline ? "page-wait-inline" : "loading-screen"} data-testid="page-wait">{label}</div>
);

export const ConfidenceBreakdown = ({ breakdown }) => {
  if (!breakdown?.components?.length) return null;
  return (
    <div className="confidence-breakdown" data-testid="confidence-breakdown">
      <div className="confidence-breakdown-head">
        <p className="kicker">DECISION CONFIDENCE · EVIDENCE QUALITY</p>
        <strong>{breakdown.headline}</strong>
        {breakdown.composite != null && (
          <span>Composite {breakdown.composite}% is calculated from the components below — not an unexplained AI claim and not authorization.</span>
        )}
      </div>
      <div className="confidence-breakdown-grid">
        {breakdown.components.map((item) => (
          <div key={item.id} data-testid={`confidence-${item.id}`}>
            <small>{item.label}</small>
            <strong>{item.value}</strong>
            {item.note && <span>{item.note}</span>}
          </div>
        ))}
      </div>
      <div className="confidence-acquire">
        <small>WHAT TO ACQUIRE NEXT</small>
        <ul>
          {(breakdown.acquire_next || []).map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>
    </div>
  );
};

export function Shell({ user, portfolio, onLogout, onStartTour, appId, app, children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const selected = appId || new URLSearchParams(location.search).get("app");
  const guide = user?.guide || portfolio?.master?.guide || {};
  const analyticsFocus = location.pathname === "/" ? (new URLSearchParams(location.search).get("focus") || "overview") : null;
  const hideCue = location.pathname === "/" && analyticsFocus === "overview";
  return (
    <GuideContext.Provider value={guide}>
    <div className="app-shell">
      <aside className={open ? "sidebar open" : "sidebar"}>
        <div className="side-top">
          <div className="brand-mark">YUG<span>360</span><small>— MIND</small></div>
          <button data-testid="sidebar-close-button" className="icon-button mobile-only" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <div className="tenant-switcher" data-testid="tenant-context">
          <span className="tenant-dot" />
          <div>
            <strong>{portfolio?.tenant?.name || "Northstar Retail Group"}</strong>
            <small>{portfolio?.tenant?.code || "NRG-01"} · <span className="sample-tag">SAMPLE WORKSPACE</span></small>
          </div>
          <ChevronRight size={15} />
        </div>
        <nav data-testid="nav-pillars">
          {PILLAR_NAV.map((pillar) => {
            const items = pillar.items.filter(([, href]) => navItemAllowedForApp(href, guide, app));
            if (!items.length) return null;
            return (
            <div key={pillar.id} className="nav-pillar" data-testid={`nav-pillar-${pillar.id}`}>
              <p className="nav-pillar-label">{pillar.label}</p>
              {items.map(([label, href, iconKey]) => {
                const Icon = NAV_ICONS[iconKey];
                const testId = `nav-${label.toLowerCase().replaceAll(" ", "-").replaceAll("–", "-")}`;
                return (
                  <NavLink
                    data-testid={testId}
                    key={href}
                    to={{ pathname: href, search: href === "/program" || !selected ? "" : `?app=${encodeURIComponent(selected)}` }}
                    end={href === "/"}
                    onClick={() => setOpen(false)}
                    className={() => (location.pathname === href || (href === "/" && location.pathname === "/") ? "nav-link active" : "nav-link")}
                  >
                    {Icon ? <Icon size={17} /> : null}
                    <span>{label}</span>
                  </NavLink>
                );
              })}
            </div>
            );
          })}
        </nav>
        <div className="side-bottom">
          {onStartTour && (
            <button data-testid="start-tour-sidebar" className="tour-launcher" onClick={onStartTour}>
              <PlayCircle size={15} /> Start guided tour
            </button>
          )}
          <div className="system-state">
            <span className="status-dot" /> {JOURNEY}
            <small>Does not execute · G1–G8 frozen · Model=None</small>
          </div>
          <button data-testid="logout-button" className="logout-button" onClick={onLogout}><LogOut size={15} /> Sign out</button>
        </div>
      </aside>
      <div className="main-area">
        <header className="topbar">
          <button data-testid="mobile-menu-button" className="icon-button mobile-only" onClick={() => setOpen(true)}><Menu size={20} /></button>
          <div className="crumb" data-testid="trace-crumb">
            <span className="sample-pill" data-testid="sample-workspace-pill">SAMPLE WORKSPACE</span>
            <ChevronRight size={13} />
            <span>Enterprise · {portfolio?.tenant?.code || "NRG-01"}</span>
            <ChevronRight size={13} />
            <Link to="/">{location.pathname === "/" ? "Analytics" : CRUMB_PAGE["/"]}</Link>
            {location.pathname === "/applications" && (
              <>
                <ChevronRight size={13} />
                <strong>Applications</strong>
              </>
            )}
            {navItemAllowed("/program", guide) && location.pathname !== "/" && location.pathname !== "/program" && (
              <>
                <ChevronRight size={13} />
                <Link to="/program">Program</Link>
              </>
            )}
            {navItemAllowed("/program", guide) && location.pathname === "/program" && (
              <>
                <ChevronRight size={13} />
                <strong>Program</strong>
              </>
            )}
            {selected && location.pathname !== "/" && location.pathname !== "/program" && (
              <>
                <ChevronRight size={13} />
                <span>{selected}</span>
              </>
            )}
            {CRUMB_PAGE[location.pathname] && location.pathname !== "/" && (
              <>
                <ChevronRight size={13} />
                <strong>{CRUMB_PAGE[location.pathname]}</strong>
              </>
            )}
          </div>
          <div className="top-actions">
            {user?.console === "tenant" && (
              <Link data-testid="open-admin-console" className="secondary-button small-btn" to="/admin/tenant">
                Admin console
              </Link>
            )}
            {onStartTour && (
              <button data-testid="start-tour-topbar" className="secondary-button small-btn" onClick={onStartTour}>
                <PlayCircle size={14} /> Guided tour
              </button>
            )}
            <span className="live-pill"><span /> LIVE DEMO</span>
            <div className="user-chip" data-testid="current-user">
              <span className="avatar">{(user?.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2)}</span>
              <span>{user?.name}<small>{user?.role}</small></span>
            </div>
            <button data-testid="logout-button-topbar" className="logout-button" type="button" onClick={onLogout}>
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </header>
        <div className="page-content">
          {user?.commercial?.trial_expired && (
            <div className="trial-expired-banner" data-testid="trial-expired-banner">
              Your trial period has ended. <Link to="/contact-sales">Contact us</Link> to continue.
            </div>
          )}
          {!hideCue && <GovernanceCue guide={user?.guide || portfolio?.master?.guide} appId={selected} />}
          {children}
        </div>
      </div>
    </div>
    </GuideContext.Provider>
  );
}

export { default as Login } from "../pages/Login";
